import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
  Toolbar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Inventory,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { createItem, updateItem, deleteItem, Item } from '../../../store/slices/masterDataSlice';

const ITEM_TYPES: Item['type'][] = [
  'raw_material',
  'finished_good',
  'semi_finished',
  'consumable',
  'spare_part',
];

const defaultForm: Partial<Item> = {
  code: '',
  name: '',
  type: 'raw_material',
  uom: '',
  category: '',
  description: '',
  status: 'active',
  minStock: undefined,
  reorderPoint: undefined,
  unitCost: undefined,
};

const ItemsTable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items, itemsLoading, error } = useSelector((state: RootState) => state.masterData);

  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [form, setForm] = useState<Partial<Item>>(defaultForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<Item | null>(null);

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      !searchText ||
      item.code.toLowerCase().includes(searchText.toLowerCase()) ||
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.category.toLowerCase().includes(searchText.toLowerCase());
    const matchesType = !typeFilter || item.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const openAddDialog = () => {
    setEditingItem(null);
    setForm(defaultForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (item: Item) => {
    setEditingItem(item);
    setForm({ ...item });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openDeleteConfirm = (item: Item) => {
    setDeletingItem(item);
    setDeleteConfirmOpen(true);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!form.code?.trim()) errors.code = 'Code is required';
    if (!form.name?.trim()) errors.name = 'Name is required';
    if (!form.uom?.trim()) errors.uom = 'UOM is required';
    if (!form.category?.trim()) errors.category = 'Category is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (editingItem) {
      await dispatch(updateItem({ id: editingItem.id, data: form }));
    } else {
      await dispatch(createItem(form));
    }
    setDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    await dispatch(deleteItem(deletingItem.id));
    setDeleteConfirmOpen(false);
    setDeletingItem(null);
  };

  const getTypeLabel = (type: Item['type']) => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getTypeColor = (type: Item['type']): 'primary' | 'secondary' | 'info' | 'warning' | 'error' => {
    switch (type) {
      case 'finished_good': return 'primary';
      case 'semi_finished': return 'info';
      case 'raw_material': return 'secondary';
      case 'consumable': return 'warning';
      case 'spare_part': return 'error';
      default: return 'primary';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'code',
      headerName: 'Code',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    {
      field: 'type',
      headerName: 'Type',
      width: 160,
      renderCell: (params) => (
        <Chip
          label={getTypeLabel(params.value)}
          color={getTypeColor(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    { field: 'uom', headerName: 'UOM', width: 80 },
    { field: 'category', headerName: 'Category', width: 140 },
    {
      field: 'unitCost',
      headerName: 'Unit Cost',
      width: 110,
      type: 'number',
      renderCell: (params) =>
        params.value != null ? `$${Number(params.value).toFixed(2)}` : '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'active' ? 'Active' : 'Inactive'}
          color={params.value === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => openEditDialog(params.row)}
          key="edit"
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => openDeleteConfirm(params.row)}
          key="delete"
          color="error"
        />,
      ],
    },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Toolbar */}
      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search items..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={typeFilter}
              label="Filter by Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {ITEM_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {getTypeLabel(t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box flex={1} />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAddDialog}
          >
            Add Item
          </Button>
        </Toolbar>
      </Paper>

      <Paper sx={{ height: 500 }}>
        <DataGrid
          rows={filteredItems}
          columns={columns}
          loading={itemsLoading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          density="compact"
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Inventory />
            {editingItem ? `Edit Item: ${editingItem.code}` : 'Add New Item'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Item Code *"
                value={form.code || ''}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                error={!!formErrors.code}
                helperText={formErrors.code}
                size="small"
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ flex: 1 }} error={!!formErrors.type}>
                <InputLabel>Type *</InputLabel>
                <Select
                  value={form.type || 'raw_material'}
                  label="Type *"
                  onChange={(e) => setForm({ ...form, type: e.target.value as Item['type'] })}
                >
                  {ITEM_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {getTypeLabel(t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Item Name *"
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              size="small"
              fullWidth
            />
            <Box display="flex" gap={2}>
              <TextField
                label="UOM *"
                value={form.uom || ''}
                onChange={(e) => setForm({ ...form, uom: e.target.value })}
                error={!!formErrors.uom}
                helperText={formErrors.uom}
                size="small"
                placeholder="pcs, kg, m..."
                sx={{ flex: 1 }}
              />
              <TextField
                label="Category *"
                value={form.category || ''}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                error={!!formErrors.category}
                helperText={formErrors.category}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Description"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Unit Cost ($)"
                type="number"
                value={form.unitCost ?? ''}
                onChange={(e) => setForm({ ...form, unitCost: e.target.value ? Number(e.target.value) : undefined })}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                label="Min Stock"
                type="number"
                value={form.minStock ?? ''}
                onChange={(e) => setForm({ ...form, minStock: e.target.value ? Number(e.target.value) : undefined })}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Reorder Point"
                type="number"
                value={form.reorderPoint ?? ''}
                onChange={(e) => setForm({ ...form, reorderPoint: e.target.value ? Number(e.target.value) : undefined })}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
            </Box>
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status || 'active'}
                label="Status"
                onChange={(e) => setForm({ ...form, status: e.target.value as Item['status'] })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingItem ? 'Update' : 'Create'} Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete item <strong>{deletingItem?.name}</strong> ({deletingItem?.code})?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemsTable;
